/**
 * CoreModule
 *
 * The `CoreModule` is a singleton providing module.
 *
 * Services and shared reducers should be provided just once when the application loads, so this file
 * is imported directly into `app.module.ts`.
 *
 * This file is intended for *app-wide* singletons. Singletons that are used only by the
 * stand-alone components in `etv-utils` should be provied in their respective
 * modules.
 */

import { CommonModule } from '@angular/common';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as AginReducer from '@cde-fe-app/app/shared/state/agin/agin.reducer';
import { environment } from '@cde-fe-app/environments/environment';
import { AginEffects } from './state/agin/agin.effects';
import { AnalyticsEffects } from './state/analytics/analytics.effects';
import * as AnalyticsReducer from './state/analytics/analytics.reducer';
import { AuthEffects } from './state/auth/auth.effects';
import * as AuthReducer from './state/auth/auth.reducer';
import { ConnectionsEffects } from './state/connections/connections.effects';
import * as ConnectionsReducer from './state/connections/connections.reducer';
import { CouplingsEffects } from './state/couplings/couplings.effects';
import * as CouplingsReducer from './state/couplings/couplings.reducer';
import { DashboardEffects } from './state/dashboard/dashboard.effects';
import * as DashboardReducer from './state/dashboard/dashboard.reducer';
import { FilterEffects } from './state/filter/filter.effects';
import * as FilterReducer from './state/filter/filter.reducer';
import { MachinesDetailsEffects } from './state/machine-details/machine-details.effects';
import * as MachineDetailsReducer from './state/machine-details/machine-details.reducer';
import { MachinesOverviewEffects } from './state/machine-overview/machines.effects';
import * as MachinesOverviewReducer from './state/machine-overview/machines.reducer';
import { OrganisationsEffects } from './state/organisations/organisations.effects';
import * as OrganisationsReducer from './state/organisations/organisations.reducer';

@NgModule({
  imports: [
    CommonModule,

    /**
     * StoreModule.forRoot is imported once in the root module, accepting a reducer
     * function or object map of reducer functions. If passed an object of
     * reducers, combineReducers will be run creating your application
     * meta-reducer. This returns all providers for an @ngrx/store
     * based application.
     */
    StoreModule.forRoot({}),
    StoreModule.forFeature('agin', AginReducer.reducer),
    StoreModule.forFeature('analytics', AnalyticsReducer.reducer),
    StoreModule.forFeature('auth', AuthReducer.reducer),
    StoreModule.forFeature('connections', ConnectionsReducer.reducer),
    StoreModule.forFeature('couplings', CouplingsReducer.reducer),
    StoreModule.forFeature('dashboard', DashboardReducer.reducer),
    StoreModule.forFeature('filter', FilterReducer.reducer),
    StoreModule.forFeature('machineDetails', MachineDetailsReducer.reducer),
    StoreModule.forFeature('machinesOverview', MachinesOverviewReducer.reducer),
    StoreModule.forFeature('organisations', OrganisationsReducer.reducer),

    /**
     * @ngrx/router-store keeps router state up-to-date in the store.
     */
    StoreRouterConnectingModule.forRoot(),

    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     */
    !environment.production ? StoreDevtoolsModule.instrument() : [],

    /**
     * EffectsModule.forRoot() is imported once in the root module and
     * sets up the effects class to be initialized immediately when the
     * application starts.
     *
     * This should include all Effects defined under `shared/state`!
     *
     * See: https://github.com/ngrx/platform/blob/master/docs/effects/api.md#forroot
     */
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([AginEffects]),
    EffectsModule.forFeature([AnalyticsEffects]),
    EffectsModule.forFeature([AuthEffects]),
    EffectsModule.forFeature([ConnectionsEffects]),
    EffectsModule.forFeature([CouplingsEffects]),
    EffectsModule.forFeature([DashboardEffects]),
    EffectsModule.forFeature([FilterEffects]),
    EffectsModule.forFeature([MachinesDetailsEffects]),
    EffectsModule.forFeature([MachinesOverviewEffects]),
    EffectsModule.forFeature([OrganisationsEffects]),
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule only');
    }
  }
}
